import ElementPropertiesManager from 'assets/core/js/module/element-properties-manager'

const toggleButtons = (plusButtonEl: HTMLButtonElement, minusButtonEl: HTMLButtonElement, inputEl: HTMLInputElement): void => {
  const currentValue = inputEl.value
  const min = inputEl.getAttribute('min')
  const max = inputEl.getAttribute('max')

  if (max && currentValue === max) {
    enableButton(minusButtonEl)
    disableButton(plusButtonEl)
  } else if (min && currentValue === min) {
    disableButton(minusButtonEl)
    enableButton(plusButtonEl)
  } else if (min && currentValue > min) {
    enableButton(plusButtonEl)
    enableButton(minusButtonEl)
  } else if (max && currentValue < max) {
    enableButton(plusButtonEl)
    enableButton(minusButtonEl)
  }
}

const enableButton = (el: HTMLButtonElement): void => {
  el.removeAttribute('disabled')
}

const disableButton = (el: HTMLButtonElement): void => {
  el.setAttribute('disabled', 'disabled')
}

export interface TravelerValues {
  [key: string]: number
}

export interface TravelerSelector {
  setValues: (values: null | TravelerValues) => void
  getValues: () => TravelerValues
  // Get the total number of persons (excluding pets).
  getTotalPersons: () => number
}

const setValues = (rootEl: HTMLElement, values: TravelerValues): void => {
  Object.keys(values).forEach((k) => {
    const value = values[k]
    const inputEl = rootEl.querySelector<HTMLInputElement>(`[data-traveler-id="${k}"]`)

    if (!inputEl || !value) {
      return
    }

    inputEl.value = value.toString()
    // don't trigger a normal change Event as it would trigger the linked callback
    inputEl.dispatchEvent(
      new CustomEvent('change', {
        detail: {
          triggerCallback: false,
        },
      })
    )
  })
}

export default (
  rootEl: HTMLElement,
  callbackClick?: (values: TravelerValues) => void,
  callbackValuesSet?: (values: TravelerValues) => void
): TravelerSelector => {
  const values: TravelerValues = {}

  rootEl.querySelectorAll<HTMLElement>('.traveler-selector').forEach((el) => {
    const plusButtonEl = el.querySelector<HTMLButtonElement>('button[data-action="plus"]')
    const minusButtonEl = el.querySelector<HTMLButtonElement>('button[data-action="minus"]')
    const inputEl = el.querySelector<HTMLInputElement>('input')

    if (!inputEl || !plusButtonEl || !minusButtonEl) {
      return
    }

    const id = inputEl.getAttribute('data-traveler-id') as string
    values[id] = parseInt(inputEl.value, 10)

    const step = Number(inputEl.step)

    inputEl.addEventListener('change', (e) => {
      const isCustomEvent = e instanceof CustomEvent

      const inputMin = parseInt(inputEl.getAttribute('min') as string, 10)
      const inputMax = parseInt(inputEl.getAttribute('max') as string, 10)
      const inputValue = parseInt(inputEl.value, 10)

      if (inputValue > inputMax) {
        inputEl.value = inputMax.toString()
      }

      if (inputValue < inputMin) {
        inputEl.value = inputMin.toString()
      }

      values[id] = parseInt(inputEl.value, 10)

      if (!isCustomEvent || (isCustomEvent && (e.detail as { triggerCallback: boolean }).triggerCallback === true)) {
        callbackClick?.(values)
      }

      toggleButtons(plusButtonEl, minusButtonEl, inputEl)
    })

    plusButtonEl.addEventListener('click', () => {
      inputEl.stepUp(step)

      values[id] = parseInt(inputEl.value, 10)

      callbackClick?.(values)
      toggleButtons(plusButtonEl, minusButtonEl, inputEl)
    })
    minusButtonEl.addEventListener('click', () => {
      inputEl.stepDown(step)

      values[id] = parseInt(inputEl.value, 10)

      callbackClick?.(values)
      toggleButtons(plusButtonEl, minusButtonEl, inputEl)
    })
  })

  const initValues = { ...values }

  const travelerSelector = {
    setValues: (values: null | TravelerValues) => {
      setValues(rootEl, values !== null ? values : initValues)
      callbackValuesSet?.(values !== null ? values : initValues)
    },
    getValues: () => values,
    getTotalPersons: () => {
      return Object.keys(values).reduce((acc, key) => {
        if (key !== 'pet') {
          return acc + (values[key] ?? 0)
        }
        return acc
      }, 0)
    },
  }

  ElementPropertiesManager.addProperty<TravelerSelector>(rootEl, 'travelerSelector', travelerSelector)

  return travelerSelector
}
